import { useState, useMemo, ChangeEvent } from 'react';
import { makeStyles, Typography, Tooltip, TextInput } from 'design-system';
import cx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CodeExecutionResult,
  CodeExecutionStatus,
  testCasePassed,
} from '@scale/shared/textcollection/defaults';
import { TestCase as ITestCase } from '@scale/shared/types/params/textcollection';

interface Judgment {
  passed: boolean;
  tooltip: string;
}

interface TestCaseProps {
  name: string;
  testCase: ITestCase;
  result?: CodeExecutionResult | null;
  editable?: boolean;
  setInput?: (s: string) => void;
  setOutput?: (s: string) => void;
  isHidden?: boolean;
  toggleVisibility?: () => void;
  onDelete?: () => void;
}

function TestCase({
  name,
  testCase,
  result,
  editable,
  setInput: _setInput,
  setOutput: _setOutput,
  isHidden,
  toggleVisibility,
  onDelete,
}: TestCaseProps) {
  const setInput = (e: ChangeEvent<HTMLInputElement>) =>
    editable && _setInput && _setInput(e.target.value);
  const setOutput = (e: ChangeEvent<HTMLInputElement>) =>
    editable && _setOutput && _setOutput(e.target.value);

  const judgment: Judgment | undefined = useMemo(() => {
    if (!result) {
      return;
    }
    let passed = false;
    let tooltip = '';
    if (result.status === CodeExecutionStatus.CompilationError) {
      tooltip = 'compilation error';
    } else if (result.status === CodeExecutionStatus.MemoryLimitExceeded) {
      tooltip = 'memory limit exceeded';
    } else if (result.status === CodeExecutionStatus.TimeLimitExceeded) {
      tooltip = 'time limit exceeded';
    } else if (result.status === CodeExecutionStatus.Success) {
      if (!testCasePassed(result, testCase)) {
        tooltip = 'wrong answer';
      } else {
        passed = true;
        tooltip = 'passed';
      }
    } else {
      tooltip = 'runtime error';
    }
    return { passed, tooltip };
  }, [result, testCase]);
  const [viewingInput, setViewingInput] = useState(true);
  const [viewingBody, setViewingBody] = useState(false);
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.header} onClick={() => setViewingBody(!viewingBody)}>
        <div className={classes.title}>
          <Typography>{name}</Typography>
          {isHidden ? (
            <Tooltip title="Results are hidden">
              <div>
                <FontAwesomeIcon className={classes.didNotRun} icon="circle" />
              </div>
            </Tooltip>
          ) : (
            judgment && (
              <Tooltip title={judgment.tooltip}>
                <div>
                  <FontAwesomeIcon
                    className={cx({
                      [classes.passed]: judgment.passed,
                      [classes.failed]: !judgment.passed,
                    })}
                    icon="circle"
                  />
                </div>
              </Tooltip>
            )
          )}
        </div>
        <div className={classes.buttonWrapper}>
          {toggleVisibility && (
            <FontAwesomeIcon
              icon={isHidden ? 'eye' : 'eye-slash'}
              className={classes.button}
              onClick={e => {
                e.stopPropagation();
                toggleVisibility();
              }}
            />
          )}
          {!editable ? (
            <FontAwesomeIcon
              icon="lock"
              className={classes.unclickableButton}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <FontAwesomeIcon
              icon="trash"
              className={classes.button}
              onClick={e => {
                e.stopPropagation();
                onDelete && onDelete();
              }}
            />
          )}
        </div>
      </div>
      {viewingBody && (
        <div className={classes.body}>
          <div className={classes.selector}>
            <Typography
              className={cx(classes.selectionText, { [classes.selected]: viewingInput })}
              onClick={() => setViewingInput(true)}
            >
              input
            </Typography>
            <Typography
              className={cx(classes.selectionText, { [classes.selected]: !viewingInput })}
              onClick={() => setViewingInput(false)}
            >
              output
            </Typography>
          </div>
          <TextInput
            multiline
            rows={6}
            maxRows={6}
            value={viewingInput ? testCase.input : testCase.output}
            onChange={viewingInput ? setInput : setOutput}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.colors.gray10}`,
    borderRadius: 4,
    boxShadow: '0px 0px 4px rgba(17, 24, 39, 0.1)',
    background: theme.colors.white,
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  buttonWrapper: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  body: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
  },
  selector: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  selectionText: {
    fontSize: 12,
    cursor: 'pointer',
  },
  selected: {
    borderBottom: `1px solid ${theme.colors.periwinkle100}`,
    cursor: 'default',
  },
  unclickableButton: {
    color: theme.colors.gray50,
    cursor: 'default',
  },
  passed: {
    color: theme.colors.green,
  },
  failed: {
    color: theme.colors.red,
  },
  didNotRun: {
    color: theme.colors.gray50,
  },
  button: {
    cursor: 'pointer',
    color: theme.colors.gray50,
    '&:hover': {
      color: theme.colors.black,
    },
  },
}));

export default TestCase;
